/**
 * Deterministic mock assistant.
 *
 * Grounded in MOCK_ITEMS only — it never names an item that is not on the menu
 * and it never states a fact the restaurant did not publish. The official
 * Dr. Calorie menu carries price, ingredients, photo, availability and (for most
 * dishes) energy in kcal; it does not carry allergens, diet labels, spice,
 * serving size or prep time, so those questions get an honest "not recorded"
 * instead of a guess. Same question, same answer — a live pitch is repeatable.
 */
import type { AIResponse, MenuItemOut, SuggestedAction } from '@/types/api';
import { MOCK_CATEGORIES, MOCK_ITEMS } from './menu';
import { formatTomanWithUnit } from '@/lib/formatting/money';
import { kcalLabel, kcalOf } from '@/lib/menuFacts';
// Prompt copy lives with the feature; the mock brain only reads it.
import { SUGGESTED_QUESTIONS } from '@/features/assistant/prompts';

const ALLERGEN_SYNONYMS: Record<string, string[]> = {
  'لبنیات': ['لبنیات', 'شیر', 'پنیر', 'خامه', 'ماست'],
  'گلوتن': ['گلوتن', 'آرد', 'نان', 'گندم'],
  'تخم‌مرغ': ['تخم مرغ', 'تخم‌مرغ'],
  'مغزها': ['مغز', 'گردو', 'بادام', 'فندق', 'پسته'],
  'بادام‌زمینی': ['بادام زمینی', 'بادام‌زمینی'],
  'کنجد': ['کنجد', 'تاهینی', 'ارده'],
  'ماهی': ['ماهی', 'سالمون', 'آنچوی'],
  'سویا': ['سویا', 'سس سویا'],
};

const SAFETY_NOTE =
  'آشپزخانه مشترک است و امکان تماس مواد وجود دارد. برای حساسیت شدید حتماً با کارکنان سالن صحبت کنید.';

const CATEGORY_NAME = new Map(MOCK_CATEGORIES.map((category) => [category.id, category.name_fa]));
const categoryOf = (item: MenuItemOut) => CATEGORY_NAME.get(item.category_id) ?? '';
const inCategories = (item: MenuItemOut, names: string[]) => names.includes(categoryOf(item));

const MAIN_CATEGORIES = ['برگر', 'پاستا', 'پلیت', 'منو ایرانی', 'ساندویچ', 'پیده', 'کلاب', 'فیتنس پک', 'آمریکایی تک نفره', 'ایتالیایی تک نفره', 'منو اکونومی'];
const TWO_CATEGORIES = ['ایتالیایی دو نفره', 'آمریکایی دونفره'];
const DESSERT_CATEGORIES = ['کیک و دسر'];
const DRINK_CATEGORIES = ['نوشیدنی', 'شیک', 'ماکتیل', 'شربت', 'آیس کافی', 'قهوه دمی', 'نوشیدنی گرم', 'چای و دمنوش', 'کافیین بار'];

function normalize(text: string): string {
  return text
    .replace(/‌/g, ' ')
    .replace(/[يى]/g, 'ی')
    .replace(/[ك]/g, 'ک')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Which declared allergens the guest's message mentions. */
export function detectAllergens(message: string): string[] {
  const text = normalize(message);
  const hit: string[] = [];
  for (const [canonical, words] of Object.entries(ALLERGEN_SYNONYMS)) {
    if (words.some((word) => text.includes(normalize(word)))) hit.push(canonical);
  }
  return hit;
}

const AVOID_INTENT = ['حساسیت', 'آلرژی', 'الرژی', 'نمی‌توانم', 'نمیتونم', 'بدون'];

/** True when the guest is asking us to avoid something, not asking about it. */
function isAvoidIntent(message: string): boolean {
  const text = normalize(message);
  return AVOID_INTENT.some((word) => text.includes(normalize(word)));
}

const available = (item: MenuItemOut): boolean => item.available;

function line(item: MenuItemOut): string {
  const kcal = kcalLabel(item);
  return `· ${item.name_fa} — ${formatTomanWithUnit(item.price_irr)}${kcal ? ` · ${kcal}` : ''}`;
}

function bulletList(items: MenuItemOut[]): string {
  return items.map(line).join('\n');
}

function viewActions(items: MenuItemOut[]): SuggestedAction[] {
  return items.map((item) => ({
    type: 'view_item',
    menu_item_id: item.id,
    quantity: 1,
    requires_confirmation: true,
  }));
}

function respond(
  answer: string,
  items: MenuItemOut[],
  extra?: { disclaimer?: string; followUps?: string[]; propose?: MenuItemOut },
): AIResponse {
  const actions = viewActions(items);
  if (extra?.propose) {
    actions.push({
      type: 'propose_add_to_cart',
      menu_item_id: extra.propose.id,
      quantity: 1,
      requires_confirmation: true,
    });
  }
  return {
    answer_fa: answer,
    referenced_item_ids: items.map((item) => item.id),
    suggested_actions: actions,
    follow_up_questions: extra?.followUps ?? [],
    disclaimer_fa: extra?.disclaimer ?? null,
    source: 'fallback',
  };
}

/** One dish per category first, so a shortlist is varied rather than five burgers. */
function spread(items: MenuItemOut[], limit: number): MenuItemOut[] {
  const seen = new Set<number>();
  const first = items.filter((item) => (seen.has(item.category_id) ? false : (seen.add(item.category_id), true)));
  return first.slice(0, limit);
}

/** Extracts a budget in toman from phrases like «۷۰۰ هزار», «۱ میلیون» or «700000». */
function parseBudgetToman(message: string): number | null {
  const latin = message
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[,٬]/g, '');
  const million = latin.match(/(\d+(?:\.\d+)?)\s*میلیون/);
  if (million?.[1]) return Math.round(Number(million[1]) * 1_000_000);
  const hezar = latin.match(/(\d+)\s*هزار/);
  if (hezar?.[1]) return Number(hezar[1]) * 1000;
  const tomanWord = latin.match(/(\d{4,})\s*(تومان|تومن)?/);
  if (tomanWord?.[1]) return Number(tomanWord[1]);
  return null;
}

const byPrice = (a: MenuItemOut, b: MenuItemOut) => a.price_irr - b.price_irr || a.id - b.id;

/**
 * The whole mock brain. Deterministic: no clock, no randomness.
 */
export function answerFor(message: string): AIResponse {
  const text = normalize(message);
  const mentioned = detectAllergens(message);
  const avoid = isAvoidIntent(message) ? mentioned : [];
  const mains = MOCK_ITEMS.filter((item) => available(item) && inCategories(item, MAIN_CATEGORIES));

  // --- 1. allergy first: safety outranks relevance, and the menu publishes no allergen data ---
  if (avoid.length > 0) {
    return respond(
      `دکتر کالری اطلاعات مواد حساسیت‌زا را در منوی خود منتشر نکرده؛ برای همین نمی‌توانم بگویم کدام غذا بدون ${avoid.join(' و ')} است و حدس هم نمی‌زنم.\nمواد هر غذا در صفحهٔ همان غذا آمده، ولی فهرست رسمی آلرژن نیست. بهتر است پیش از سفارش از کارکنان بپرسید.`,
      [],
      { disclaimer: SAFETY_NOTE },
    );
  }

  // --- 2. budget ---
  const budget = parseBudgetToman(message);
  if (budget && budget >= 20_000) {
    const budgetIrr = budget * 10;
    const drinks = MOCK_ITEMS.filter((item) => available(item) && inCategories(item, ['نوشیدنی'])).sort(byPrice);
    const drink = drinks[0];
    const room = budgetIrr - (drink?.price_irr ?? 0);
    const affordable = spread(mains.filter((item) => item.price_irr <= room).sort((a, b) => b.price_irr - a.price_irr || a.id - b.id), 3);
    if (affordable.length === 0 || !drink) {
      const cheapest = MOCK_ITEMS.filter(available).sort(byPrice).slice(0, 3);
      return respond(
        `با این بودجه ترکیب کاملی جا نمی‌شود، اما این‌ها زیر بودجهٔ شما هستند:\n${bulletList(cheapest)}`,
        cheapest,
      );
    }
    const lines = affordable
      .map((main) => `· ${main.name_fa} + ${drink.name_fa} = ${formatTomanWithUnit(main.price_irr + drink.price_irr)}`)
      .join('\n');
    return respond(
      `با بودجهٔ ${formatTomanWithUnit(budgetIrr)} این ترکیب‌ها جا می‌شوند:\n${lines}\nهمه زیر بودجه می‌مانند.`,
      [...affordable, drink],
      { followUps: ['کدامش سبک‌تر است؟', 'دسر هم اضافه کنم؟'] },
    );
  }

  // --- 3. spice ---
  if (text.includes('تند')) {
    return respond(
      'میزان تندی غذاها در منوی رسمی دکتر کالری ثبت نشده؛ پس نمی‌توانم بگویم کدام ملایم یا تند است.\nاگر مواد غذا (مثل سس یا فلفل) در صفحهٔ آن آمده، همان را بخوانید یا در یادداشت سفارش بنویسید که تند نشود.',
      [],
    );
  }

  // --- 4. vegetarian / vegan ---
  if (text.includes('گیاه') || text.includes('وگان') || text.includes('بدون گوشت')) {
    const salads = MOCK_ITEMS.filter((item) => available(item) && inCategories(item, ['سالاد', 'سیب زمینی'])).slice(0, 4);
    return respond(
      `دکتر کالری برچسب «گیاهی» یا «وگان» روی غذاهایش نگذاشته، پس نمی‌توانم تأیید کنم کدام بدون گوشت است.\nبرای شروع می‌توانید فهرست مواد این‌ها را ببینید:\n${bulletList(salads)}`,
      salads,
      { disclaimer: 'برای رژیم خاص، پیش از سفارش با کارکنان تأیید کنید.' },
    );
  }

  // --- 5. light / diet: real kcal from the official menu ---
  if (text.includes('سبک') || text.includes('کم کالری') || text.includes('کالری') || text.includes('رژیم') || text.includes('سالم')) {
    const light = spread(
      MOCK_ITEMS.filter((item) => available(item) && (kcalOf(item) ?? Infinity) <= 450 && !inCategories(item, DRINK_CATEGORIES))
        .sort((a, b) => (kcalOf(a) ?? 0) - (kcalOf(b) ?? 0) || a.id - b.id),
      4,
    );
    return respond(
      `کم‌کالری‌ترین انتخاب‌ها طبق انرژی چاپ‌شده در منوی رسمی:\n${bulletList(light)}`,
      light,
      { followUps: ['نوشیدنی سبک چی پیشنهاد می‌کنی؟'] },
    );
  }

  // --- 6. for two / group ---
  if (text.includes('دو نفر') || text.includes('دونفر') || text.includes('دو تا')) {
    const pairs = MOCK_ITEMS.filter((item) => available(item) && inCategories(item, TWO_CATEGORIES));
    const picks = spread(pairs, 3);
    return respond(
      `برای دو نفر، این‌ها در منو «دونفره» تعریف شده‌اند:\n${bulletList(picks)}\nزمان آماده‌سازی در منوی رسمی ثبت نشده.`,
      picks,
      { followUps: ['یک دسر هم کنارش پیشنهاد می‌دی؟'] },
    );
  }

  // --- 7. fast ---
  if (text.includes('سریع') || text.includes('عجله') || text.includes('زود')) {
    return respond('زمان آماده‌سازی غذاها در منوی رسمی ثبت نشده و از صف آشپزخانه هم خبر ندارم؛ پس نمی‌توانم بگویم کدام سریع‌تر می‌رسد. از کارکنان بپرسید.', []);
  }

  // --- 8. popular / discounts ---
  if (text.includes('محبوب') || text.includes('پرسفارش') || text.includes('بهترین') || text.includes('تخفیف')) {
    const discounted = spread(MOCK_ITEMS.filter((item) => available(item) && item.details?.discount_label), 4);
    return respond(
      `آمار فروش این نسخه را ندارم، پس «پرفروش‌ترین» را حدس نمی‌زنم. چیزی که در منو ثبت است تخفیف است:\n${discounted.map((item) => `${line(item)} · ${item.details?.discount_label}`).join('\n')}`,
      discounted,
      { followUps: ['با بودجهٔ مشخص چی پیشنهاد می‌کنی؟', 'برای دو نفر چی خوبه؟'] },
    );
  }

  // --- 9. dessert / drink ---
  if (text.includes('دسر') || text.includes('شیرین')) {
    const desserts = MOCK_ITEMS.filter((item) => available(item) && inCategories(item, DESSERT_CATEGORIES)).slice(0, 3);
    return respond(`دسرها و کیک‌های موجود:\n${bulletList(desserts)}`, desserts);
  }
  if (text.includes('نوشیدنی') || text.includes('خنک') || text.includes('بنوشم')) {
    const drinks = spread(MOCK_ITEMS.filter((item) => available(item) && inCategories(item, DRINK_CATEGORIES)), 4);
    return respond(`چند نوشیدنی موجود:\n${bulletList(drinks)}`, drinks);
  }

  // --- 10. a specific item by name ---
  const named = [...MOCK_ITEMS].sort((a, b) => b.name_fa.length - a.name_fa.length).find((item) => text.includes(normalize(item.name_fa)));
  if (named) {
    const kcal = kcalLabel(named);
    const parts = [
      `${named.name_fa} — ${formatTomanWithUnit(named.price_irr)}`,
      named.ingredients.length ? `مواد: ${named.ingredients.slice(0, 4).join('، ')}${named.ingredients.length > 4 ? ' و …' : ''}.` : 'فهرست مواد این غذا در منوی رسمی ثبت نشده.',
      kcal ? `انرژی: ${kcal}.` : '',
      available(named) ? '' : 'این غذا فعلاً ناموجود است.',
      'آلرژن و تندی در منوی رسمی ثبت نشده.',
    ].filter(Boolean);
    return respond(parts.join('\n'), [named], {
      disclaimer: SAFETY_NOTE,
      propose: available(named) ? named : undefined,
      followUps: ['با چی خوب جمع می‌شه؟'],
    });
  }

  // --- fallback ---
  const safe = spread(mains, 3);
  return respond(
    `سه انتخاب از منوی موجود:\n${bulletList(safe)}\nاگر بودجه، تعداد نفرات یا سقف کالری را بگویید، دقیق‌تر پیشنهاد می‌دهم.`,
    safe,
    { followUps: SUGGESTED_QUESTIONS.slice(0, 3) },
  );
}
